/**
 * Pure derivations for the fleet panel. Kept out of the component so the
 * drift rule and the partial-snapshot tolerance are testable on their own.
 */

export function hasInventory(project) {
  return Boolean(project && project.inventorySnapshot);
}

/**
 * A manually created project — no CMDB snapshot and no managed product. Its
 * components card is fed by the modules on its own issues, not a fleet scan.
 */
export function isManualComponents(project) {
  return Boolean(project && !project.inventorySnapshot && !project.managedProductId);
}

/**
 * Drift compares inventory strings with inventory strings. The snapshot's
 * `baselineInventoryVersion` is the raw CMDB version the project's
 * `versionId` was created from (recorded by the create endpoint, carried
 * across refreshes); `majorityVersion` is the raw CMDB majority now. A
 * project created before that field existed falls back to comparing
 * `versionId` itself, which is exact only where a vendor's suggestion id IS
 * the version string (F5) and would otherwise flag drift on every refresh
 * (ESXi: "8.0.1 P02" vs "8.0.1 build-22088125").
 */
export function fleetPanelData(project) {
  if (!hasInventory(project)) return null;
  const s = project.inventorySnapshot || {};
  const baseline = project.versionId || null;
  const baselineInventoryVersion = s.baselineInventoryVersion || null;
  // What the fleet would have to report for a device to count as "on the
  // baseline" — the inventory form when we have it, the release id otherwise.
  const baselineKey = baselineInventoryVersion || baseline;
  const majorityVersion = s.majorityVersion || null;
  const totalCis = s.totalCis || 0;
  const versions = Array.isArray(s.versions) ? s.versions : [];

  // The backend's `matchingCount` (service-common/lib/python/plan_inventory/core.py)
  // counts the fleet's MAJORITY version, not the project's baseline — correct
  // for its own create-time purpose (baseline === majority then) but wrong to
  // display once they diverge: it would report the count of the version the
  // panel isn't even talking about. Derive the baseline's own count from the
  // versions[] breakdown instead; a baseline absent from the breakdown is a
  // true 0, not missing data — itself a strong drift signal.
  const baselineEntry = baselineKey ? versions.find(v => v.version === baselineKey) : null;
  const matchingCount = baselineEntry ? baselineEntry.ciCount || 0 : 0;
  const inconsistentCount = Math.max(totalCis - matchingCount, 0);

  return {
    baseline,
    baselineInventoryVersion,
    majorityVersion,
    totalCis,
    matchingCount,
    inconsistentCount,
    versions,
    fetchedAt: s.fetchedAt || null,
    hasDrift: Boolean(baselineKey && majorityVersion && majorityVersion !== baselineKey)
  };
}

export function formatFetchedAt(iso) {
  if (!iso) return { text: '—', title: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { text: '—', title: iso };
  return {
    text: d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    title: iso
  };
}
